import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {BaseValue} from '@mintdata/mint-kit/api/values/BaseValue';
import {AFrameColumn} from '@mintdata/mint-kit/api/values/frames/AFrameColumn';
import {AFrameRecord} from '@mintdata/mint-kit/api/values/frames/AFrameRecord';
import {
    ColumnOffset,
    FieldName,
    FrameCellCoordinate,
    FrameData,
    FramePath,
    IFrame,
    IFrameCell,
    IFrameColumn,
    IFrameField,
    IFrameRecord,
    IKeyedMap,
    RowIndex,
    SortComparator
} from '@mintdata/mint-kit/api/values/frames/Frame';
import {
    InvalidFramePath,
    InvalidRowIndex,
    NoSuchCell,
    NoSuchColumn,
    NoSuchField
} from '@mintdata/mint-kit/api/values/frames/FrameErrors';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {FrameMeta} from '@mintdata/mint-kit/api/values/frames/FrameMeta';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {IFrameRowsVisitor} from '@mintdata/mint-kit/api/values/frames/FrameVisitor';
import {FieldRef} from '@mintdata/mint-kit/impl/frames/FieldRef';
import {ArrayUtils} from '@mintdata/mint-kit/impl/util/ArrayUtils';
import _times from 'lodash/times';

export class AFrame extends BaseValue implements IFrame
{
    private readonly _fields:Array<FrameField>;
    private readonly _data:FrameData;
    private readonly _meta:FrameMeta;

    public constructor(fields:Array<FrameField>, data:FrameData, meta?:FrameMeta)
    {
        super(Type.Frame);
        this._fields = fields;
        this._data = data;
        this._meta = meta ? meta : new FrameMeta();
    }

    public get isError():boolean
    {
        return this.hasErrors;
    }

    public getType():Type
    {
        return Type.Frame;
    }

    public toValue():string
    {
        return this.toString(); // TODO: Fix serialization/deserialization
    }

    public isEqualTo(other:IValue):boolean
    {
        return null;
    }

    public compareTo(other:IValue):number
    {
        return null;
    }

    public clone():IValue
    {
        return Frames.fromJSONObject(this.toJSON());
    }

    public static empty():IFrame
    {
        return Frames.empty();
    }

    public get hasErrors():boolean
    {
        if( !AFrame._hasErrorsCache.has(this) )
            AFrame._hasErrorsCache.set(this, this._computeHasErrors());

        return AFrame._hasErrorsCache.get(this);
    }

    private _computeHasErrors():boolean
    {
        const rowCount:number = this._data.length;

        for( let i = 0; i < rowCount; i++ )
        {
            const row:Array<IValue> = this._data[i];

            for( let j = 0; j < row.length; j++ )
            {
                const cell:IValue = row[j];

                if( cell.getType() === Type.Error )
                    return true;
            }
        }

        return false;
    }

    public get isEmpty():boolean
    {
        return this._data.length === 0;
    }

    public get recordCount():number
    {
        return this._data.length;
    }

    public get fieldCount():number
    {
        return this._fields.length;
    }

    getAllValues():IValue[]
    {
        if( this.isEmpty )
            return [];

        const values:IValue[] = [];
        for( let row of this._data )
        {
            for( let value of row )
                values.push(value);
        }

        return values;
    }

    public getRowsCount():number
    {
        return this._data.length;
    }

    public getRecords():Array<IFrameRecord>
    {
        if( !AFrame._recordsCache.has(this) )
            AFrame._recordsCache.set(this, this._createRecords());

        return AFrame._recordsCache.get(this);
    }

    private _createRecords():Array<IFrameRecord>
    {
        return this._data.map((row:Array<IValue>) =>
        {
            const record:IKeyedMap = new Map<string, IValue>();

            let col:number = 0;

            for( let field of this._fields )
            {
                record.set(field.name, row[col]);
                col++;
            }

            return new AFrameRecord(record);
        });
    }

    public getFieldsUnsafe():Array<FrameField>
    {
        return this._fields;
    }

    public getFields():Array<FrameField>
    {
        return this._fields.map((field:FrameField) =>
        {
            return field.clone();
        });
    }

    public getFieldNames():Array<FieldName>
    {
        return this._fields.map((field:FrameField) =>
        {
            return field.name;
        });
    }

    public getFieldNamesByPath(pathInFrame:FramePath):Array<FieldName>
    {
        const [cellCoordinate, ...restPath] = pathInFrame;

        if( cellCoordinate )
        {
            const cellValue = this.getCell(cellCoordinate.fieldName, cellCoordinate.rowIndex);

            if( cellValue && Type.Frame.isEqualTo(cellValue.getType()) )
            {
                const frame = cellValue as IFrame;

                if( restPath.length )
                    return frame.getFieldNamesByPath(restPath);
                else
                    return frame.getFieldNames();
            }

            return [];
        }

        return [];
    }

    public getColumn<T extends IValue>(fieldName:FieldName):IFrameColumn<T> | NoSuchColumn
    {
        const field:FrameField = this.findField(fieldName);

        if( field === null )
            throw new NoSuchField(`Cannot find field ${fieldName} in frame`);

        const colOffset:number = this._getColumnOffset(fieldName);

        if( colOffset === -1 )
            return null as NoSuchColumn;

        const rows:Array<T> = [];

        this._data.map((row:Array<IValue>) =>
        {
            const cellValue:T = row[colOffset] as T;
            rows.push(cellValue);
        });

        return new AFrameColumn<T>(field.name, field.type, rows);
    }

    public getColumnAsFrame<T extends IValue>(fieldName:FieldName):IFrame | NoSuchColumn
    {
        const field:FrameField = this.findField(fieldName);

        if( field === null )
            return null as NoSuchColumn;

        const colOffset:number = this._getColumnOffset(fieldName);

        if( colOffset === -1 )
            return null as NoSuchColumn;

        const newData:FrameData = [];

        this._data.map((row:Array<IValue>) =>
        {
            const cellValue:T = row[colOffset] as T;
            newData.push([cellValue]);
        });

        return new AFrame([new FrameField(field.name, field.type)], newData);
    }

    private _getColumnOffset(fieldName:FieldName, isIgnoreCase:boolean = false):number | -1
    {
        let i:number = 0;

        for( let field of this._fields )
        {
            if( isIgnoreCase && field.name.toLowerCase() === fieldName.toLowerCase() )
                return i;
            else if( field.name === fieldName )
                return i;

            i++;
        }

        return -1;
    }

    public findField(fieldName:FieldName):FrameField | null
    {
        for( let field of this._fields )
        {
            if( field.name === fieldName )
                return field;
        }

        return null;
    }

    private _isValidRowIndex(rowIndex:RowIndex):boolean
    {
        return rowIndex >= 0 && rowIndex < this._data.length;
    }

    private _isValidColumnOffset(columnOffset:ColumnOffset):boolean
    {
        return columnOffset >= 0 && columnOffset < this._fields.length;
    }

    public getCell(fieldName:FieldName, rowIndex:RowIndex):IValue | NoSuchCell
    {
        if( !(this._isValidRowIndex(rowIndex)) )
            return null as NoSuchCell;

        const colOffset:number = this._getColumnOffset(fieldName);

        if( colOffset === -1 )
            return null as NoSuchCell;

        const cellValue:IValue = this._data[rowIndex][colOffset];
        return cellValue;
    }

    getCellByOffset(columnOffset:ColumnOffset, rowIndex:RowIndex):IValue | NoSuchCell
    {
        if( !(this._isValidRowIndex(rowIndex)) )
            return null as NoSuchCell;

        if( !(this._isValidColumnOffset(columnOffset)) )
            return null as NoSuchCell;

        const cellValue:IValue = this._data[rowIndex][columnOffset];
        return cellValue;
    }

    public getCellByPath(pathInFrame:FramePath):IValue | NoSuchCell
    {
        // return immidiately in a strange case of null coordinates in path...
        // can this even happen ?
        if( !pathInFrame || pathInFrame.some(coord => !coord) )
            return null;

        let currentValue:IValue = this;
        for( const coord of pathInFrame )
        {
            if( !Type.Frame.isEqualTo(currentValue.getType()) )
                return null;

            currentValue = (currentValue as IFrame).getCell(coord.fieldName, coord.rowIndex);

            if( !currentValue )
                break;
        }

        return currentValue;
    }

    private static _resolveNestedFieldRef(curFrame:AFrame, pathInFrame:FieldName[], retFrame:AFrame):void
    {
        const [curPathPart, ...restPath] = pathInFrame;

        if( !curPathPart )
            return;

        const foundCol = curFrame.getColumnAsFrame(curPathPart);
        if( foundCol === (null as NoSuchColumn) )
            return;

        const isLastPathPart = restPath.length === 0;

        if( isLastPathPart )
        {
            const foundField = foundCol.getFields()[0];
            const offset = retFrame._getColumnOffset(curPathPart, true);

            if( offset === -1 )
                (retFrame as any)._fields = [foundField];

            const accumulatedData = (foundCol as any)._data;
            for( const row of accumulatedData )
                retFrame._data.push(row as any);
        }
        else
        {
            // const colType = foundCol.getFields()[0].type;
            // if( !Type.Frame.isEqualTo(colType) )

            const internalData = (foundCol as any)._data;

            const hasNestedFrames = internalData &&
                internalData.length > 0 &&
                internalData[0] &&
                internalData[0].length > 0 &&
                Type.Frame.isEqualTo(internalData[0][0].type);

            if( !hasNestedFrames )
                return;

            const rows = (foundCol as any)._data;
            let aFrameRow:AFrame;

            for( aFrameRow of rows )
            {
                const aFrameCell = aFrameRow[0];
                this._resolveNestedFieldRef(aFrameCell, restPath, retFrame);
            }
        }
    }

    public getMeta():FrameMeta
    {
        return this._meta;
    }

    public getData():Array<Array<IValue>>
    {
        return this._data.map(row => row.slice());
    }

    public getDataUnsafe():FrameData
    {
        return this._data;
    }

    public withCellByPath(pathInFrame:FramePath, value:IValue):IFrame
    {
        const newFrame:IFrame = new AFrame(this._fields, [...this._data], this._meta);

        if( pathInFrame.length === 0 )
            throw new InvalidFramePath('pathInFrame cannot be empty');

        const coord:FrameCellCoordinate = pathInFrame[0];
        const coordColIndex:number = newFrame.getFieldNames().indexOf(coord.fieldName);

        const nestedValue:IValue = newFrame.getCell(coord.fieldName, coord.rowIndex);

        if( !nestedValue )
            throw new InvalidFramePath('pathInFrame is not valid');

        newFrame.getDataUnsafe()[coord.rowIndex] = [...newFrame.getDataUnsafe()[coord.rowIndex]];

        if( Type.Frame.isEqualTo(nestedValue.getType()) && pathInFrame.length > 1 )
            newFrame.getDataUnsafe()[coord.rowIndex][coordColIndex] = (nestedValue as IFrame).withCellByPath(pathInFrame.slice(1), value);
        else
            newFrame.getDataUnsafe()[coord.rowIndex][coordColIndex] = value;

        return newFrame;
    }

    public withFieldRenamed<T extends IValue>(currentFieldName:FieldName, newFieldName:FieldName):IFrame
    {
        const columnOffset = this._getColumnOffset(currentFieldName);

        if( columnOffset === -1 )
            throw new NoSuchField(`withFieldRenamed(): frame does not contain field named: ${currentFieldName}`);

        const newFields:Array<FrameField> = this._fields.slice();

        newFields[columnOffset] = new FrameField(newFieldName, newFields[columnOffset].type);

        const newData:FrameData = [];
        const numRows:number = this._data.length;

        for( let i = 0; i < numRows; i++ )
        {
            const row = this._data[i];
            let rowCopy = row.slice();

            newData[i] = rowCopy;
        }

        return new AFrame(newFields, newData, this._meta);
    }

    public withColumnSubset<T extends IValue>(fieldRefs:Array<FieldRef>, isIgnoreCase:boolean = false):IFrame
    {
        if( !fieldRefs )
            throw new NoSuchField('frame.withColumnSubset(): field names must be specified, cannot be null/undefined');

        if( !Array.isArray(fieldRefs) )
            throw new NoSuchField('frame.withColumnSubset(): field names must be an array ([])');

        const newFields:Array<FrameField> = [];
        const selectedColumnOffsets:Array<ColumnOffset> = [];

        for( let fieldRef of fieldRefs )
        {
            if( fieldRef.isSimpleRef )
            {
                const fieldName = fieldRef.fieldName;
                const colOffset:number = this._getColumnOffset(fieldName, isIgnoreCase);

                if( colOffset === -1 )
                    throw new NoSuchField(`frame.withColumnSubset(): field with name '${fieldName}' does not exist in frame`);

                selectedColumnOffsets.push(colOffset);

                const field:FrameField = this._fields[colOffset];
                newFields.push(field);
            }
            else
            {
                selectedColumnOffsets.push(-1);
                newFields.push(new FrameField(fieldRef.toLiteral(), Type.Frame));
            }
        }

        const newData:FrameData = [];
        const numRows:number = this._data.length;

        for( let i = 0; i < numRows; i++ )
        {
            const row:Array<IValue> = this._data[i];
            const newRow:Array<IValue> = newData[i] = [];

            let j = 0;
            for( let selectedColumnOffset of selectedColumnOffsets )
            {
                if( selectedColumnOffset !== -1 )
                {
                    const colValue:IValue = row[selectedColumnOffset];
                    newRow.push(colValue);
                }
                else
                {
                    const fieldRef = fieldRefs[j];
                    const retFrame = new AFrame([], []);
                    AFrame._resolveNestedFieldRef(this, fieldRef.fieldPath, retFrame);

                    newRow.push(retFrame);
                }

                j++;
            }
        }

        return new AFrame(newFields, newData, this._meta);
    }

    public withColumns<T extends IValue>(columns:Array<IFrameColumn<T>>, index = this._fields.length):IFrame
    {
        const newFields:Array<FrameField> = [
            ...this._fields.slice(0, index),
            ...columns.map(column => new FrameField(column.fieldName, column.fieldType)),
            ...this._fields.slice(index)
        ];

        const newData:FrameData = this._data.map((row:Array<IValue>, rowIndex:number) =>
        {
            const cells:Array<IValue> = columns.map((column:IFrameColumn<T>) => (
                column.rows[rowIndex] !== undefined ? column.rows[rowIndex] : AEmpty.Value
            ));

            return [
                ...row.slice(0, index),
                ...cells,
                ...row.slice(index)
            ];
        });

        return new AFrame(newFields, newData, this._meta);
    }

    public withoutColumns(indices:Array<number>):IFrame
    {
        const newFields:Array<FrameField> = this._fields.filter(
            (field:FrameField, index:number) => indices.indexOf(index) === -1
        );

        const newData:FrameData = this._data.map((row:Array<IValue>, rowIndex:number) => (
            row.filter((value:IValue, index:number) => indices.indexOf(index) === -1)
        ));

        return new AFrame(newFields, newData, this._meta);
    }

    public withColumnsReordered(oldIndex:ColumnOffset, newIndex:ColumnOffset, length:number):IFrame
    {
        const newFields:Array<FrameField> = ArrayUtils.reorderArray(this._fields, oldIndex, newIndex, length);
        const newData:FrameData = this._data.map(
            (row:Array<IValue>) => ArrayUtils.reorderArray(row, oldIndex, newIndex, length)
        );

        return new AFrame(newFields, newData, this._meta);
    }

    public withRecordsReordered(oldIndex:ColumnOffset, newIndex:ColumnOffset, length:number):IFrame
    {
        const newData:FrameData = ArrayUtils.reorderArray(this._data, oldIndex, newIndex, length);

        return new AFrame(this._fields, newData, this._meta);
    }

    public withRecordsSorted(fieldName:FieldName, comparator:SortComparator, sortedIndexMap?:Array<number>):IFrame
    {
        const column:IFrameColumn<IValue> = this.getColumn(fieldName);
        let indexMap:Array<number> = sortedIndexMap;

        if( !indexMap )
        {
            indexMap = column.rows.map((item, index) => index);
            indexMap.sort((a:number, b:number) => (
                comparator(column.rows[a].toString(), column.rows[b].toString())
            ));
        }

        const newData:FrameData = this._data.map((row:Array<IValue>, index:number) => this._data[indexMap[index]]);

        return new AFrame(this._fields, newData, this._meta);
    }

    public withRecordsSubset(rowIndicies:Array<RowIndex>):IFrame
    {
        if( !rowIndicies )
            throw new InvalidRowIndex('frame.withRecordsSubset(): rowIndicies cannot be null/undefined');

        if( !Array.isArray(rowIndicies) )
            throw new InvalidRowIndex('frame.withRecordsSubset(): rowIndicies must be a JavaScript array ([])');

        const newFields:Array<FrameField> = this._fields.slice();
        const newData:FrameData = [];

        if( rowIndicies.length > 0 )
        {
            const numRows:number = this._data.length;

            for( let i = 0; i < numRows; i++ )
            {
                if( rowIndicies.indexOf(i) !== -1 )
                {
                    const row:Array<IValue> = this._data[i];
                    const rowCopy:Array<IValue> = row.slice();

                    newData.push(rowCopy);
                }
            }
        }

        return new AFrame(newFields, newData, this._meta);
    }

    public withRecords(records:FrameData, index:number = this._data.length):IFrame
    {
        const newData:FrameData = [
            ...this._data.slice(0, index),
            ...records,
            ...this._data.slice(index)
        ];

        return new AFrame(this._fields, newData, this._meta);
    }

    public withoutRecords(indices:Array<number>):IFrame
    {
        const newData:FrameData = this._data.filter(
            (row:Array<IValue>, index:number) => indices.indexOf(index) === -1
        );

        return new AFrame(this._fields, newData, this._meta);
    }

    public withCells<T extends IValue>(cells:Array<IFrameCell<T>>):IFrame
    {
        const fieldNames:Array<FieldName> = this.getFieldNames();
        const newData:FrameData = this._data.map((row:Array<IValue>, rowIndex:number) =>
        {
            const newCells:Array<IFrameCell<T>> = cells.filter(item => item.rowIndex === rowIndex);

            if( newCells.length )
            {
                return row.map((cell:IValue, columnIndex:number) =>
                {
                    const fieldName:FieldName = fieldNames[columnIndex];
                    const newCell:IFrameCell<T> = newCells.find(
                        (item:IFrameCell<T>) => item.fieldName === fieldName
                    );

                    return newCell ? newCell.value : cell;
                });
            }

            return row;
        });

        return new AFrame(this._fields, newData, this._meta);
    }

    public withoutCells<T extends IValue>(cells:Array<FrameCellCoordinate>):IFrame
    {
        const fieldNames:Array<FieldName> = this.getFieldNames();
        const newData:FrameData = this._data.map((row:Array<IValue>, rowIndex:number) =>
        {
            const cellsToEmpty:Array<FrameCellCoordinate> = cells.filter(
                (item:FrameCellCoordinate) => item.rowIndex === rowIndex
            );

            if( cellsToEmpty.length )
            {
                return row.map((cell:IValue, columnIndex:number) =>
                {
                    const fieldName:FieldName = fieldNames[columnIndex];
                    const cellToEmpty:FrameCellCoordinate = cellsToEmpty.find(
                        (item:FrameCellCoordinate) => item.fieldName === fieldName
                    );

                    return cellToEmpty ? AEmpty.Value : cell;
                });
            }

            return row;
        });

        return new AFrame(this._fields, newData, this._meta);
    }

    public withMeta(meta:FrameMeta):IFrame
    {
        const newFields:Array<FrameField> = this._fields.slice();

        const newData:FrameData = [];
        const numRows = this._data.length;

        for( let i = 0; i < numRows; i++ )
        {
            const row:Array<IValue> = this._data[i];
            const rowCopy:Array<IValue> = row.slice();
            newData[i] = rowCopy;
        }

        return new AFrame(newFields, newData, meta);
    }

    public append(other:IFrame, resultRecordsCountLimit?:number):IFrame
    {
        const otherFields = other.getFields();
        const resultFields = this._fields.slice();
        otherFields.forEach((newFrameField:FrameField) =>
        {
            if( !this.findField(newFrameField.name) )
                resultFields.push(newFrameField);
        });

        const extraFieldsCount = resultFields.length - this.fieldCount;

        const thisRecordCount = this.recordCount;
        const otherRecordsCount = other.recordCount;
        const resultRecordsCount = thisRecordCount + otherRecordsCount;
        let thisIncludedRecordsStartIndex = 0;
        let otherIncludedRecordsStartIndex = 0;
        if( resultRecordsCountLimit && resultRecordsCount > resultRecordsCountLimit )
        {
            thisIncludedRecordsStartIndex = Math.min(thisRecordCount, resultRecordsCount - resultRecordsCountLimit);
            otherIncludedRecordsStartIndex = Math.max(0, otherRecordsCount - resultRecordsCountLimit);
        }

        const resultData:FrameData = [];
        for( let i = thisIncludedRecordsStartIndex; i < thisRecordCount; ++i )
        {
            resultData.push([
                ...this._data[i],
                ...new Array(extraFieldsCount).fill(AEmpty.Value)]
            );
        }

        const otherRecords = other.getRecords();
        for( let i = otherIncludedRecordsStartIndex; i < otherRecordsCount; ++i )
        {
            const record = otherRecords[i];

            const row = resultFields.map(field =>
            {
                if( record.hasCell(field.name) )
                {
                    const cellValue = record.getCell(field.name);
                    if( !cellValue )
                        return AEmpty.Value;

                    return cellValue;
                }

                return AEmpty.Value;
            });

            resultData.push(row);
        }

        return new AFrame(resultFields, resultData);
    }

    public extendWith(frame:IFrame):IFrame
    {
        const newFieldNameToIndexMap = new Map<FieldName, number>();
        frame.getFieldNames().forEach((value, index) => newFieldNameToIndexMap.set(value, index));

        const newFields:Array<FrameField> = frame.getFields();

        const recordCount:number = Math.max(this._data.length, frame.recordCount);
        const fields:Array<FrameField> = [...this._fields, ...newFields.filter((field) => !this.findField(field.name))];

        const data:FrameData = _times(recordCount, (recordIndex:number) =>
        {
            return fields.map(({name}:FrameField, fieldIndex:number) =>
            {
                if( fieldIndex < this.fieldCount ) // get values from current frame
                {
                    if( recordIndex < this.recordCount )
                    {
                        return this._data[recordIndex][fieldIndex];
                    }
                }
                else // get values from extension frame
                {
                    if( recordIndex < frame.recordCount )
                    {
                        const columnOffset = newFieldNameToIndexMap.get(fields[fieldIndex].name);
                        return frame.getCellByOffset(columnOffset, recordIndex);
                    }
                }

                return AEmpty.Value;
            });
        });

        return new AFrame(fields, data);
    }

    join(frame2:IFrame, joinFieldName1:FieldName, joinFieldName2?:FieldName, frame1Alias?:string, frame2Alias?:string):IFrame | AError
    {
        return Frames.join(this, joinFieldName1, frame2, joinFieldName2, frame1Alias, frame2Alias);
    }

    public toJSON():Object
    {
        return Frames.toJSON(this);
    }

    public toString():string
    {
        return JSON.stringify(this.toJSON());
    }

    public async acceptAsync(visitor:IFrameRowsVisitor):Promise<any>
    {
        await visitor.preFrame(this);

        const fields:Array<IFrameField> = this.getFieldsUnsafe();

        await Promise.all(fields.map(visitor.frameField));

        const data:Array<Array<IValue>> = this.getDataUnsafe();

        const processRow = async (rowData:Array<IValue>, rowIndex:number) =>
        {
            await visitor.preFrameRow(rowData, rowIndex);

            await Promise.all(fields.map((field:IFrameField, colIndex:number) =>
            {
                return visitor.frameCell(field, rowData[colIndex], colIndex, rowIndex);
            }));

            return visitor.postFrameRow(rowData, rowIndex);
        };

        await Promise.all(data.map(processRow));

        return visitor.postFrame(this);
    }

    public static isFrame:boolean = true;

    private static _recordsCache:WeakMap<IFrame, Array<IFrameRecord>> = new WeakMap<IFrame, Array<IFrameRecord>>();
    private static _hasErrorsCache:WeakMap<IFrame, boolean> = new WeakMap<IFrame, boolean>();
}
